import { Contributor } from '@/hexapla-core/domains/accounts'
import { Lemma } from '@/hexapla-core/domains/lemmas'
import { DEFAULT_BASE_TEXTS } from '@/hexapla-core/domains/lemmas/edition'
import {
  Book,
  Witness,
  WitnessType,
  TranslatorRelationship,
  Translator,
  MT,
  LXX,
  Meta,
} from '@/hexapla-core/domains/resources'

export interface FlashMessage {
  message: string
  level: string
}

export type NewLemma = Omit<Lemma, 'id'>

export interface FetchQueue {
  id: string
  action: <T>() => Promise<T>
}

export interface Reader {
  bookName: string
  meta: Meta | null
  bookID: number
  chapter: number
  src: string
  mt: MT[]
  lxx: LXX[]
}

export interface HexaplaState {
  user: {
    token: string
    isLoggedIn: boolean
    contributor: Partial<Contributor> | null
    settings: Record<string, boolean>
    roles: string[]
    lemmas: Lemma[]
    /** Garret Fellow — work routes through a supervisor for review. */
    isFellow: boolean
    /** Set only for fellows: who reviews their submissions. */
    supervisorId: number | null
    /** True when at least one fellow reports to this contributor. */
    isSupervisor: boolean
    /** Books this user may act on. For a fellow this is their granted subset,
     *  never the supervisor's full set. Presentation only — the API enforces it. */
    scopedBookIds: number[] | null
  }
  viewMessages: Record<string, boolean>
  theme: Record<string, unknown>
  flashQueue: FlashMessage[]
  appIsLoading: boolean
  loadingQueue: Record<string, unknown>
  books: Book[]
  translators: Translator[]
  translatorRelationships: TranslatorRelationship[]
  witnesses: Witness[]
  witnessTypes: WitnessType[]
  reader: Reader
  newLemma: Lemma
  /**
   * Who `newLemma` belongs to after a session ended underneath them (see
   * SESSION_ENDED). Null while signed in, and after a deliberate sign-out.
   */
  draftOwnerId: number | null
  /**
   * The id of the lemma the contributor opened with Edit, so the wizard knows
   * holding it is intended (see domains/lemmas/wizardDraft). Cleared whenever
   * the wizard's lemma is.
   */
  editingLemmaId: Lemma['id'] | null
  rfc: Record<string, unknown>[]
  serviceRequests: Record<string, unknown>[]
  preFetchQueue: FetchQueue[]
}

export const DEFAULT_NEW_LEMMA: Lemma = {
  id: '',
  lemma: '',
  hebrew: '',
  lemma_start: null,
  lemma_stop: null,
  attestations: [],
  flagged: false,
  classic: false,
  sub_verse: null,
  base_texts: [...DEFAULT_BASE_TEXTS],
  texts: [],
  create_contributor_id: null,
  update_contributor_id: null,
}

export default {
  user: {
    token: '',
    isLoggedIn: false,
    contributor: null,
    roles: [],
    lemmas: [],
    settings: {},
    isFellow: false,
    supervisorId: null,
    isSupervisor: false,
    scopedBookIds: [],
  },
  viewMessages: {},
  theme: {},
  flashQueue: [],
  appIsLoading: false,
  loadingQueue: {},
  books: [],
  translators: [],
  translatorRelationships: [],
  witnesses: [],
  witnessTypes: [],
  reader: {
    bookName: '',
    meta: null,
    bookID: 0,
    chapter: 0,
    src: '',
    mt: [],
    lxx: [],
  },
  newLemma: { ...DEFAULT_NEW_LEMMA },
  draftOwnerId: null,
  editingLemmaId: null,
  rfc: [],
  serviceRequests: [],
  preFetchQueue: [], // [{ id, action }]
} as HexaplaState
